import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import { DatePicker } from "@/components/common/DatePicker";
import { PROJECT_STATUSES } from "@/constants";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const ProjectModal = ({ onClose, onSaved, project }) => {
  const { user } = useAuth();
  const canEditDates = !project || user?.role === "admin" || (project.created_by || project.owner_id) === user?.id;
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    name: project?.name || "", description: project?.description || "", product_line: project?.product_line || "",
    start_date: project?.start_date?.slice(0, 10) || "", target_production_date: project?.target_production_date?.slice(0, 10) || "",
    status: project?.status || "Concept", budget: project?.budget || "",
    hold_reason: project?.hold_reason || "",
    editors: project?.editors || [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => u.status === "active" && u.id !== user?.id))).catch(() => {});
  }, [user?.id]);

  const toggleEditor = (uid) => setForm((f) => ({
    ...f, editors: f.editors.includes(uid) ? f.editors.filter((e) => e !== uid) : [...f.editors, uid],
  }));

  const handleSubmit = async (e, isDraft = false) => {
    e.preventDefault();
    if (form.status === "On Hold" && !form.hold_reason.trim()) {
      toast.error("Please add a remark explaining why the project is on hold");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        is_draft: isDraft,
        budget: form.budget ? Number(form.budget) : null,
        start_date: form.start_date || null,
        target_production_date: form.target_production_date || null,
      };
      if (project) await api.put(`/projects/${project.id}`, payload);
      else await api.post("/projects", payload);
      toast.success(isDraft ? "Draft saved — you can edit everything until you publish" : (project ? "Project updated" : "Project created"));
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" style={{ borderRadius: "2px" }} data-testid="project-modal">
        <DialogHeader><DialogTitle className="font-display">{project ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-xs font-semibold">Project Name</Label>
            <Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required style={{ borderRadius: "2px" }} />
          </div>
          <div>
            <Label className="text-xs font-semibold">Description</Label>
            <Textarea data-testid="project-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} style={{ borderRadius: "2px" }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Product Line</Label>
              <Input data-testid="project-product-line-input" value={form.product_line} onChange={(e) => setForm({ ...form, product_line: e.target.value })} style={{ borderRadius: "2px" }} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Budget (₹)</Label>
              <Input data-testid="project-budget-input" type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} style={{ borderRadius: "2px" }} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold">Start Date</Label>
              <DatePicker testId="project-start-date-input" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} disabled={!canEditDates} />
            </div>
            <div>
              <Label className="text-xs font-semibold">Target Production Date</Label>
              <DatePicker testId="project-target-date-input" value={form.target_production_date} onChange={(v) => setForm({ ...form, target_production_date: v })} disabled={!canEditDates} />
            </div>
          </div>
          {!canEditDates && (
            <p className="text-[11px] text-muted-foreground font-mono" data-testid="project-dates-locked-hint">&gt; timeline locked — only the project creator can change start/target dates</p>
          )}
          <div>
            <Label className="text-xs font-semibold">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="project-status-select" style={{ borderRadius: "2px" }}><SelectValue /></SelectTrigger>
              <SelectContent>{PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.status === "On Hold" && (
            <div>
              <Label className="text-xs font-semibold">Hold Remarks <span className="text-destructive">*</span></Label>
              <Textarea data-testid="project-hold-reason-input" value={form.hold_reason}
                onChange={(e) => setForm({ ...form, hold_reason: e.target.value })}
                placeholder="Why is this project on hold? (e.g. awaiting customer approval, material shortage...)"
                style={{ borderRadius: "2px" }} />
              <p className="text-[11px] text-muted-foreground font-mono mt-1">&gt; while on hold, no overdue/delays are tracked — tracking resumes when the project is taken off hold</p>
            </div>
          )}
          <div>
            <Label className="text-xs font-semibold">Permitted Editors</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" data-testid="project-editors-select"
                  className="w-full border border-input bg-background px-3 py-2 text-sm text-left"
                  style={{ borderRadius: "2px" }}>
                  {form.editors.length > 0
                    ? users.filter((u) => form.editors.includes(u.id)).map((u) => u.name).join(", ") || `${form.editors.length} selected`
                    : "None — only you can add phases/tasks"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72 max-h-56 overflow-y-auto" style={{ borderRadius: "2px" }}>
                {users.map((u) => (
                  <DropdownMenuCheckboxItem key={u.id} data-testid={`project-editor-option-${u.id}`}
                    checked={form.editors.includes(u.id)} onCheckedChange={() => toggleEditor(u.id)}
                    onSelect={(e) => e.preventDefault()}>
                    {u.name} <span className="text-muted-foreground ml-1 text-xs">({u.role})</span>
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <p className="text-[11px] text-muted-foreground font-mono mt-1">&gt; permitted editors can add phases & tasks to this project</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" data-testid="project-modal-cancel-btn" onClick={onClose} className="btn-outline-navy">Cancel</button>
            {(!project || project.is_draft) ? (
              <button type="button" data-testid="project-modal-save-draft-btn" disabled={saving} onClick={(e) => handleSubmit(e, true)} className="btn-navy">
                {saving ? "Saving..." : "Save Draft"}
              </button>
            ) : (
              <button type="submit" data-testid="project-modal-save-btn" disabled={saving} className="btn-navy">
                {saving ? "Saving..." : "Save Changes"}
              </button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
